import { IAnnouncerPack } from "./IAnnouncerPack";
import DefaultAnnouncerPack from "./DefaultAnnouncerPack";
import FireworksFragAnnouncerPack from "./FireworksFragAnnouncerPack";
import KitKitMiauMiauFragAnnouncerPack from "./KitKitMiauMiauFragAnnouncerPack";
import { IAudioManager } from "../../types/audio-manager";

export type AnnouncerPackType = "default" | "fireworks" | "kit-kit-miau-miau";

/**
 * Factory for creating and caching announcer packs
 * Supports category-based pack selection (frag, lines, taunt, etc.)
 */
export class AnnouncerPackFactory {
  private fragPackCache: Map<AnnouncerPackType, IAnnouncerPack> = new Map();
  private audioManager?: IAudioManager;
  // Future: linesPackCache, tauntPackCache, etc.

  initialize(audioManager: IAudioManager): void {
    this.audioManager = audioManager;
  }

  preloadPack(type: AnnouncerPackType): void {
    if (!this.audioManager) return;
    const pack = this.getFragAnnouncerPack(type);
    pack.initialize(this.audioManager);
  }

  /**
   * Gets a frag announcer pack by type
   */
  getFragAnnouncerPack(type: AnnouncerPackType = "default"): IAnnouncerPack {
    if (this.fragPackCache.has(type)) {
      return this.fragPackCache.get(type)!;
    }

    const pack = this.createPack(type);
    this.fragPackCache.set(type, pack);
    if (this.audioManager) {
      pack.initialize(this.audioManager);
    }
    return pack;
  }

  private createPack(type: AnnouncerPackType): IAnnouncerPack {
    switch (type) {
      case "fireworks":
        return new FireworksFragAnnouncerPack();
      case "kit-kit-miau-miau":
        return new KitKitMiauMiauFragAnnouncerPack();
      case "default":
      default:
        return new DefaultAnnouncerPack();
    }
  }

  /**
   * Gets frag announcer pack from localStorage
   */
  getFragAnnouncerPackFromStorage(): IAnnouncerPack {
    try {
      const stored = localStorage.getItem("fragAnnouncerPack");
      const packType = stored ? JSON.parse(stored) : "default";
      return this.getFragAnnouncerPack(packType as AnnouncerPackType);
    } catch {
      return this.getFragAnnouncerPack("default");
    }
  }

  /**
   * Clears the frag pack cache
   */
  clearFragPackCache(): void {
    this.fragPackCache.clear();
  }

  // Future extensibility: getLinesAnnouncerPack(), getTauntAnnouncerPack(), etc.
}

